import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import {
  Copy, Pencil, ChevronDown, ChevronRight,
  DollarSign, Download, Mail, UserCheck,
  Send, BookCheck, AlertTriangle, Sparkles,
  Clock, FileText,
} from 'lucide-react';
import { SUGGESTED_CODES } from './doctorModeData';
import { useSpecialty } from '../../../contexts/SpecialtyContext';

export const SOAPPanel = ({
  soapContent, setSoapContent,
  patient,
  activeTab, setActiveTab,
  editingSections, setEditingSections,
  transcript,
  requestCopy,
  onCopyFull, onCopyAP, onCopyCodes, onDownloadCCDA,
  onShowDM, onShowInstructions, onShowSummary, onShowBilling,
  onDone,
  onReviewNote,
}) => {
  const { specialty } = useSpecialty();
  /* Merge specialty-specific CPTs on top of the visit's inferred codes.
     De-duplicate by code. */
  const specialtyCpts = (specialty.common_cpts || []).map((c) => ({
    ...c, confidence: 88, type: c.code?.startsWith('G') || c.code?.startsWith('H') ? 'HCPCS' : 'CPT',
    desc: c.desc,
    specialty_tuned: true,
  }));
  const seen = new Set(SUGGESTED_CODES.map((c) => c.code));
  const mergedCodes = [
    ...SUGGESTED_CODES,
    ...specialtyCpts.filter((c) => !seen.has(c.code)),
  ];
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
      <div className="flex items-center justify-between">
        <TabsList className="bg-slate-100 h-8">
          <TabsTrigger value="soap" className="text-xs h-6 px-3" data-testid="tab-note">Note</TabsTrigger>
          <TabsTrigger value="transcript" className="text-xs h-6 px-3" data-testid="tab-transcript">Transcript</TabsTrigger>
          <TabsTrigger value="billing" className="text-xs h-6 px-3" data-testid="tab-billing">Billing</TabsTrigger>
        </TabsList>
        <Button size="sm" variant="ghost" className="text-xs text-slate-400 h-7" onClick={onDone} data-testid="done-btn">Done</Button>
      </div>

      {/* Note Tab */}
      <TabsContent value="soap" className="space-y-3 mt-0">
        {/* Consolidated Action Bar */}
        <div className="bg-slate-900 rounded-xl p-3" data-testid="soap-action-bar">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onCopyFull} className="bg-white text-slate-900 hover:bg-slate-100 gap-1.5 font-semibold h-8 text-xs" data-testid="copy-full-btn">
              <Copy className="h-3 w-3" /> Copy full note
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 gap-1 h-8 text-xs" data-testid="more-actions-btn">
                  More actions <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" align="start">
                <button onClick={onCopyAP} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2"><Copy className="h-3 w-3" /> Copy A&P only</button>
                <button onClick={onCopyCodes} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2"><DollarSign className="h-3 w-3" /> Copy billing codes</button>
                <button onClick={onDownloadCCDA} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2"><Download className="h-3 w-3" /> Download CCDA</button>
                <button onClick={onShowDM} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2"><Mail className="h-3 w-3" /> Direct message</button>
                <button onClick={onShowInstructions} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2"><UserCheck className="h-3 w-3" /> Patient instructions</button>
                <button onClick={onShowSummary} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2"><Mail className="h-3 w-3" /> Email summary</button>
                <button onClick={() => {
                  const anyEditing = Object.values(editingSections).some(v => v);
                  setEditingSections(anyEditing ? {} : { subjective: true, objective: true, assessment: true, plan: true });
                }} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-2" data-testid="edit-all-btn">
                  <Pencil className="h-3 w-3" /> {Object.values(editingSections).some(v => v) ? 'Done editing' : 'Edit all sections'}
                </button>
              </PopoverContent>
            </Popover>
            <div className="flex-1" />
            <Button size="sm" className="bg-slate-700 hover:bg-slate-800 text-white gap-1.5 h-8 text-xs font-semibold" onClick={() => { toast.success('Note finalized', { description: 'Marked as finalized. Copy to your EHR.' }); }} data-testid="finalize-note-btn">
              <BookCheck className="h-3 w-3" /> Finalize note
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-8 text-xs font-semibold" onClick={onShowBilling} data-testid="sign-billing-btn">
              <Send className="h-3 w-3" /> Sign & send to billing
            </Button>
          </div>
        </div>

        {/* Clinical AI Alert */}
        {patient.vitals?.bp && parseInt(patient.vitals.bp.split('/')[0]) > 135 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/50" data-testid="clinical-flag">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            <p className="text-[11px] text-amber-700">AI Alert: BP {patient.vitals.bp} exceeds 130/80 target for this patient's profile. Confirm treatment plan.</p>
          </div>
        )}

        {/* SOAP Sections with AI Alerts */}
        {['Subjective', 'Objective', 'Assessment', 'Plan'].map(section => {
          const key = section.toLowerCase();
          const isEditing = editingSections[key];
          const aiAlerts = {
            subjective: patient.aiBrief?.attentionItems?.length > 0 ? 'Consider documenting reported symptom duration and aggravating factors' : null,
            objective: patient.vitals?.bp && parseInt(patient.vitals.bp.split('/')[0]) > 135 ? `BP ${patient.vitals.bp} \u2014 document target and rationale` : null,
            assessment: patient.riskAdjustment?.gapOpportunities?.length > 0 ? `HCC gap: ${patient.riskAdjustment.gapOpportunities[0]}` : null,
            plan: null,
          };
          return (
            <div key={section} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`soap-section-${key}`}>
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50/60">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{section}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => requestCopy(soapContent[key], section)} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5"><Copy className="h-2.5 w-2.5" /> Copy</button>
                  <button onClick={() => setEditingSections(p => ({ ...p, [key]: !p[key] }))} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5" data-testid={`edit-${key}-btn`}><Pencil className="h-2.5 w-2.5" /> {isEditing ? 'Done' : 'Edit'}</button>
                </div>
              </div>
              {aiAlerts[key] && (
                <div className="px-4 py-1.5 bg-blue-50/50 border-b border-blue-100 flex items-center gap-2" data-testid={`ai-alert-${key}`}>
                  <Sparkles className="h-3 w-3 text-blue-500 shrink-0" />
                  <p className="text-[10px] text-blue-700">{aiAlerts[key]}</p>
                </div>
              )}
              {isEditing ? (
                <div className="p-4"><Textarea value={soapContent[key]} onChange={e => setSoapContent({ ...soapContent, [key]: e.target.value })} className="min-h-28 font-mono text-sm border-0 shadow-none focus-visible:ring-0 p-0" /></div>
              ) : (
                <div className="px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{soapContent[key]}</div>
              )}
            </div>
          );
        })}
      </TabsContent>

      {/* Transcript Tab */}
      <TabsContent value="transcript" className="mt-0">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="transcript-panel">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Session Transcript</p>
          {transcript.length > 0 ? (
            <div className="space-y-3">
              {transcript.map((line, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <span className="text-slate-400 font-mono w-10 shrink-0 text-xs pt-0.5">{line.time}</span>
                  <div>
                    <span className={cn("text-xs font-semibold", line.speaker === 'Patient' ? "text-blue-600" : "text-violet-600")}>{line.speaker}</span>
                    <p className="text-slate-600 mt-0.5">{line.text}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-8">No transcript recorded for this session.</p>}
        </div>
      </TabsContent>

      {/* Billing Tab */}
      <TabsContent value="billing" className="mt-0 space-y-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="billing-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              <p className="text-xs font-bold text-slate-800">Coding & Billing</p>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={onCopyCodes} data-testid="billing-copy-codes">
              <Copy className="h-2.5 w-2.5" /> Copy All Codes
            </Button>
          </div>
          <div className="space-y-2">
            {mergedCodes.map(c => (
              <div key={c.code} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/30 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">{c.code}</Badge>
                  <span className="text-xs text-slate-700">{c.desc}</span>
                  {c.specialty_tuned && (
                    <Badge className="bg-slate-900 text-white text-[8px] border-0" title={`${specialty.label} common code`}>
                      {specialty.short}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-100 text-slate-600 text-[9px] border-0">{c.type}</Badge>
                  <Badge className={cn("text-[9px] border-0", (c.confidence || 90) >= 95 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>{c.confidence || 90}%</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Previous Encounter Notes */}
        {patient.soapNotes?.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="encounter-review-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-slate-800">Previous Encounter Notes</p>
              <p className="text-[10px] text-slate-400">{patient.soapNotes?.length || 0} notes</p>
            </div>
            <div className="space-y-2">
              {patient.soapNotes.map(note => (
                <div key={note.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => onReviewNote(note)}
                  data-testid={`review-note-${note.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{note.chief}</p>
                      <p className="text-xs text-slate-400">{note.date} &middot; {note.provider}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={cn("text-[9px] border-0", note.status === 'Signed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700')}>{note.status || 'Draft'}</Badge>
                    <ChevronRight className="h-3 w-3 text-slate-300" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </TabsContent>

      {/* Sticky Bottom Action Bar */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 rounded-xl p-3 mt-3 flex items-center justify-between shadow-sm" data-testid="sticky-encounter-bar">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            <span>Encounter: {patient.firstName} {patient.lastName}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={onDone} data-testid="close-encounter-btn">
            Close encounter
          </Button>
          <Button size="sm" className="bg-slate-700 hover:bg-slate-800 text-white h-8 text-xs gap-1.5 font-semibold" onClick={() => { toast.success('Note finalized'); }} data-testid="sticky-finalize-btn">
            <BookCheck className="h-3 w-3" /> Finalize note
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs gap-1.5 font-semibold" onClick={onShowBilling} data-testid="sticky-sign-btn">
            <Send className="h-3 w-3" /> Sign & send to billing
          </Button>
        </div>
      </div>
    </Tabs>
  );
};
